import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Plus,
  Edit,
  Trash2,
  Eye,
  BookOpen,
  Save,
  Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

export default function HelpAdmin({ user }) {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [summary, setSummary] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [order, setOrder] = useState(0);

  useEffect(() => {
    // Check admin access
    if (!['admin', 'convenor', 'secretary'].includes(user?.role)) {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    
    fetchCategories();
    fetchArticles();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/api/help/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/help/admin/articles`, {
        withCredentials: true
      });
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error("Error fetching articles:", error);
      toast.error("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setTitle(article.title);
      setCategoryId(article.category_id);
      setSummary(article.summary || "");
      setContent(article.content);
      setTags(article.tags?.join(", ") || "");
      setIsPublished(article.is_published);
      setOrder(article.order || 0);
    } else {
      setEditingArticle(null);
      setTitle("");
      setCategoryId(categories[0]?.id || "");
      setSummary("");
      setContent("");
      setTags("");
      setIsPublished(true);
      setOrder(0);
    }
    setShowEditor(true);
  };

  const saveArticle = async () => {
    if (!title.trim() || !content.trim() || !categoryId) {
      toast.error("Please fill in title, category, and content");
      return;
    }
    
    setSaving(true);
    try {
      const articleData = {
        title,
        category_id: categoryId,
        summary: summary || content.substring(0, 200) + "...",
        content,
        tags: tags.split(",").map(t => t.trim()).filter(t => t),
        is_published: isPublished,
        order
      };
      
      if (editingArticle) {
        // Update
        await axios.put(`${API}/api/help/admin/articles/${editingArticle.id}`, articleData, {
          withCredentials: true
        });
        toast.success("Article updated");
      } else {
        // Create
        await axios.post(`${API}/api/help/admin/articles`, articleData, {
          withCredentials: true
        });
        toast.success("Article created");
      }
      
      setShowEditor(false);
      fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  const deleteArticle = async (article) => {
    if (!window.confirm(`Delete "${article.title}"? This cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/api/help/admin/articles/${article.id}`, {
        withCredentials: true
      });
      toast.success("Article deleted");
      fetchArticles();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const seedArticles = async () => {
    if (!window.confirm("This will add initial help articles. Continue?")) return;
    
    try {
      const response = await axios.post(`${API}/api/help/admin/seed-initial-articles`, {}, {
        withCredentials: true
      });
      toast.success(response.data.message);
      fetchArticles();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to seed articles");
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  // Group articles by category
  const articlesByCategory = articles.reduce((acc, article) => {
    const cat = article.category_id;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(article);
    return acc;
  }, {});

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin")}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Admin
            </Button>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={seedArticles}
              className="border-slate-600"
            >
              Seed Initial Articles
            </Button>
            <Button
              onClick={() => openEditor()}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Article
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <BookOpen className="w-8 h-8 text-yellow-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Help Center Admin</h1>
            <p className="text-slate-400">Manage help articles and documentation</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="p-4 text-center">
            <p className="text-3xl font-bold text-white">{articles.length}</p>
            <p className="text-sm text-slate-400">Total Articles</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-3xl font-bold text-green-400">
              {articles.filter(a => a.is_published).length}
            </p>
            <p className="text-sm text-slate-400">Published</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-400">
              {articles.filter(a => !a.is_published).length}
            </p>
            <p className="text-sm text-slate-400">Drafts</p>
          </Card>
        </div>

        {/* Articles by Category */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading...</div>
        ) : articles.length === 0 ? (
          <Card className="p-8 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-500" />
            <h3 className="text-lg font-bold text-white mb-2">No Articles Yet</h3>
            <p className="text-slate-400 mb-4">
              Click "Seed Initial Articles" to add starter content, or create your own.
            </p>
          </Card>
        ) : (
          <div className="space-y-6">
            {categories.map((category) => {
              const catArticles = articlesByCategory[category.id] || [];
              if (catArticles.length === 0) return null;
              
              return (
                <div key={category.id}>
                  <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                    {category.name}
                    <span className="text-sm font-normal text-slate-400">({catArticles.length})</span>
                  </h2>
                  <div className="space-y-2">
                    {catArticles.map((article) => (
                      <Card key={article.id} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-medium text-white">{article.title}</h3>
                              {!article.is_published && (
                                <span className="text-xs bg-slate-600 text-slate-300 px-2 py-0.5 rounded">
                                  Draft
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 line-clamp-1">
                              {article.summary}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {article.views || 0} views
                              </span>
                              <span>
                                Updated: {new Date(article.updated_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`/help/${article.slug}`, '_blank')}
                              className="text-slate-400 hover:text-white"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditor(article)}
                              className="text-blue-400 hover:text-blue-300"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteArticle(article)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Editor Dialog */}
        <Dialog open={showEditor} onOpenChange={setShowEditor}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingArticle ? "Edit Article" : "New Article"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Title *</label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Article title"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Category *</label>
                  <select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    className="w-full p-2 bg-slate-800 border border-slate-700 rounded text-white"
                  >
                    <option value="">Select category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-slate-400 block mb-1">Summary</label>
                <Input
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  placeholder="Brief description (shown in article lists)"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 block mb-1">Content * (Markdown supported)</label>
                <Textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="# Heading&#10;&#10;Write your article content here...&#10;&#10;## Subheading&#10;&#10;- List item 1&#10;- List item 2&#10;&#10;**Bold text** and regular text."
                  className="bg-slate-800 border-slate-700 min-h-[300px] font-mono text-sm"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Tags (comma-separated)</label>
                  <Input
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="calendar, sync, outlook"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Sort Order</label>
                  <Input
                    type="number"
                    value={order}
                    onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={isPublished}
                    onCheckedChange={setIsPublished}
                  />
                  <span className="text-slate-300">Published</span>
                </label>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditor(false)}
                    className="border-slate-600"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveArticle}
                    disabled={saving}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
